/*
  En-tête Marquoir (refonte R4, REFONTE.md §6).
  - Sur ordinateur (990 px et plus), l'en-tête reste à l'écran : il porte le bouton « Composer · prix → », et un
    bouton laissé seul en haut à droite recouvrait le texte des colonnes de droite (guide, accueil) et se perdait
    sur le pied de page encre. Sous 990 px, la barre d'achat du bas porte l'appel et l'en-tête se cache :
  - <mq-entete> se cache en descendant et revient en remontant, selon une COURSE et non au moindre pixel :
    caché après 8 px de descente continue au-delà de 96 px, rendu après 24 px de remontée continue
    (un geste voulu, pas un rebond de molette ou de doigt). Jamais caché si le focus clavier est dedans,
    si le menu est ouvert ou si le mouvement est réduit.
  - Vitré (papier translucide) dès qu'un contenu passe dessous. Sur l'ouverture épinglée de l'accueil (.hc),
    rien ne passe dessous au repos : l'en-tête y reste transparent en haut de page et ne se vitre qu'au-delà
    du seuil de masquage, donc pendant qu'il est caché (section-header.css retarde le changement de fond le
    temps de la sortie). Aucune bande vitrée n'apparaît pour s'en aller aussitôt.
  - Menu mobile en <dialog> modal : focus piégé, Échap ferme, arrière-plan inerte (natif), défilement bloqué
    (CSS, et Lenis arrêté s'il est actif). Une adresse qui finit par #menu ouvre le menu (lien direct).
  - « Panier (n) » : le nombre est mis à jour sur l'événement mq:panier-maj {item_count}.
  - Sélecteur de langue (<mq-langue>, snippet selecteur-langue) : liste dépliante non modale. Le bouton porte
    aria-expanded ; Échap referme la liste et rend le focus au bouton (dans le menu mobile, Échap ne ferme que la
    liste) ; un clic ailleurs, la sortie du focus ou la fermeture du menu la referment. L'en-tête ne se cache pas
    tant que la liste de la barre est ouverte.
  - Pied de page : envoi automatique des sélecteurs langue et pays (sauf au clavier, où « Valider » reste le geste).
*/

const ECRAN_LARGE = window.matchMedia('(min-width: 990px)');
const MOUVEMENT_REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)');
const SEUIL_CACHE = 96; // px défilés avant de pouvoir cacher l'en-tête (le travelling de l'ouverture commence là)
const COURSE_CACHER = 8; // px de descente continue avant de cacher
const COURSE_MONTRER = 24; // px de remontée continue avant de rendre l'en-tête
const FOCALISABLES = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function lenisActif() {
  const motion = window.Marquoir && window.Marquoir.motion;
  return (motion && motion.lenis) || null;
}

class MqEntete extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('dialog.mq-menu');
    this.ouvreur = this.querySelector('[data-mq-menu-ouvrir]');
    this.dernierY = Math.max(0, window.scrollY);
    this.sens = 0;
    this.course = 0;
    this.image = 0;
    this.imageMesure = 0;
    this.seuilVitre = 0;
    this.lenisArrete = false;
    this.doigt = false; // un doigt touche l'écran
    this.sensDoigt = 0; // sens du dernier geste du doigt (le lancer garde ce sens)

    this.surDefilement = () => {
      if (!this.image) this.image = requestAnimationFrame(() => this.majDefilement());
    };
    this.surMesure = () => {
      if (this.imageMesure) return;
      this.imageMesure = requestAnimationFrame(() => {
        this.imageMesure = 0;
        this.majOuverture();
        this.majDefilement();
      });
    };
    this.surSection = () => {
      this.observerOuverture();
      this.surMesure();
    };
    // Retour arrière depuis le cache du navigateur : l'en-tête revient visible, la course repart de zéro.
    this.surRetour = (event) => {
      if (event.persisted) {
        this.removeAttribute('data-cache');
        this.dernierY = Math.max(0, window.scrollY);
        this.sens = 0;
        this.course = 0;
      }
      this.surMesure();
    };
    this.surFocus = () => this.removeAttribute('data-cache');
    this.surOuvrir = (event) => {
      event.preventDefault(); // le JS prend la main sur l'ouverture native (commandfor)
      this.ouvrir();
    };
    this.surFermer = (event) => {
      event.preventDefault();
      this.fermer();
    };
    this.surClavier = (event) => this.pieger(event);
    this.surClicMenu = (event) => {
      if (event.target instanceof Element && event.target.closest('a[href]')) this.fermer();
    };
    this.surFermeture = () => this.apresFermeture();
    this.surLargeur = (event) => {
      if (event.matches) {
        this.fermer();
        this.removeAttribute('data-cache');
      }
    };
    this.surMouvement = () => {
      if (MOUVEMENT_REDUIT.matches) this.removeAttribute('data-cache');
    };
    this.surPanier = (event) => this.majPanier(event.detail && event.detail.item_count);

    window.addEventListener('scroll', this.surDefilement, { passive: true });
    this.surDoigt = (event) => { this.doigt = event.type === 'touchstart' || event.type === 'touchmove'; };
    ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach((type) => window.addEventListener(type, this.surDoigt, { passive: true }));
    window.addEventListener('resize', this.surMesure, { passive: true });
    window.addEventListener('pageshow', this.surRetour);
    document.addEventListener('shopify:section:load', this.surSection);
    this.addEventListener('focusin', this.surFocus);
    this.ouvreur?.addEventListener('click', this.surOuvrir);
    this.menu?.querySelectorAll('[data-mq-menu-fermer]').forEach((bouton) => {
      bouton.addEventListener('click', this.surFermer);
    });
    this.menu?.addEventListener('keydown', this.surClavier);
    this.menu?.addEventListener('click', this.surClicMenu);
    this.menu?.addEventListener('close', this.surFermeture);
    ECRAN_LARGE.addEventListener('change', this.surLargeur);
    MOUVEMENT_REDUIT.addEventListener('change', this.surMouvement);
    document.addEventListener('mq:panier-maj', this.surPanier);

    this.observerOuverture();
    this.majOuverture();
    this.majDefilement();
    if (window.location.hash.startsWith('#menu') && !ECRAN_LARGE.matches) this.ouvrir();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.surDefilement);
    ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach((type) => window.removeEventListener(type, this.surDoigt));
    window.removeEventListener('resize', this.surMesure);
    window.removeEventListener('pageshow', this.surRetour);
    document.removeEventListener('shopify:section:load', this.surSection);
    ECRAN_LARGE.removeEventListener('change', this.surLargeur);
    MOUVEMENT_REDUIT.removeEventListener('change', this.surMouvement);
    document.removeEventListener('mq:panier-maj', this.surPanier);
    this.observateur?.disconnect();
    if (this.image) cancelAnimationFrame(this.image);
    if (this.imageMesure) cancelAnimationFrame(this.imageMesure);
    this.image = 0;
    this.imageMesure = 0;
    if (this.menu?.open) this.menu.close();
    this.relancerLenis();
  }

  // L'ouverture épinglée de l'accueil : seule sa classe « empilée » (sans épinglage) change le comportement.
  observerOuverture() {
    this.observateur?.disconnect();
    this.ouverture = document.querySelector('main .hc');
    if (!this.ouverture || !('MutationObserver' in window)) return;
    this.empilee = this.ouverture.classList.contains('hc--empile');
    this.observateur = new MutationObserver(() => {
      const empilee = this.ouverture.classList.contains('hc--empile');
      if (empilee === this.empilee) return;
      this.empilee = empilee;
      this.surMesure();
    });
    this.observateur.observe(this.ouverture, { attributes: true, attributeFilter: ['class'] });
  }

  // Lu au chargement, au redimensionnement et quand l'ouverture change de mode : jamais pendant le défilement.
  majOuverture() {
    const ouverture = this.ouverture;
    let epinglee = false;
    if (ouverture && ouverture.isConnected && !ouverture.classList.contains('hc--empile')) {
      const scene = ouverture.querySelector('.hc-scene') || ouverture.firstElementChild;
      if (scene && getComputedStyle(scene).position === 'sticky') {
        const haut = ouverture.getBoundingClientRect().top + window.scrollY;
        epinglee = haut <= this.offsetHeight + 1; // l'ouverture commence sous l'en-tête, en haut de page
      }
    }
    this.seuilVitre = epinglee ? SEUIL_CACHE : 0;
    this.toggleAttribute('data-ouverture', epinglee);
  }

  majDefilement() {
    this.image = 0;
    const y = Math.max(0, window.scrollY);
    const ecart = y - this.dernierY;
    this.dernierY = y;

    const vitre = y > this.seuilVitre;
    if (vitre !== this.hasAttribute('data-defile')) this.toggleAttribute('data-defile', vitre);

    // Aimantation de l'ouverture (heros-chiffre) : ce défilement n'est pas un geste de l'utilisateur, l'en-tête ne bouge
    // pas. Avec Lenis, html porte data-mq-aimant pendant le glissé. Au doigt (scroll-snap, html.mq-hc-aimant), la
    // remontée qui suit un lancer vers le bas, doigt levé, est la pose sur une planche : elle ne rend pas l'en-tête.
    const html = document.documentElement;
    const sensEcart = Math.sign(ecart);
    if (this.doigt && sensEcart) this.sensDoigt = sensEcart;
    const aimant = html.hasAttribute('data-mq-aimant')
      || (html.classList.contains('mq-hc-aimant') && !this.doigt && sensEcart !== 0 && this.sensDoigt !== 0 && sensEcart !== this.sensDoigt);

    if (ecart !== 0 && !aimant) {
      const sens = ecart > 0 ? 1 : -1;
      if (sens !== this.sens) {
        this.sens = sens;
        this.course = 0;
      }
      this.course += Math.abs(ecart);
    }

    let cache = this.hasAttribute('data-cache');
    if (y <= SEUIL_CACHE) cache = false;
    else if (this.sens > 0 && this.course >= COURSE_CACHER) cache = this.peutCacher();
    else if (this.sens < 0 && this.course >= COURSE_MONTRER) cache = false;
    if (cache !== this.hasAttribute('data-cache')) this.toggleAttribute('data-cache', cache);
  }

  peutCacher() {
    return !ECRAN_LARGE.matches && !MOUVEMENT_REDUIT.matches && !this.menu?.open && !this.focusClavierDedans() && !this.langueOuverte();
  }

  langueOuverte() {
    return this.querySelector('.mq-entete__barre [data-mq-langue-bouton][aria-expanded="true"]') !== null;
  }

  focusClavierDedans() {
    try {
      return this.querySelector(':focus-visible') !== null;
    } catch {
      return this.contains(document.activeElement);
    }
  }

  ouvrir() {
    if (!this.menu || this.menu.open) return;
    this.removeAttribute('data-cache');
    this.menu.showModal();
    const lenis = lenisActif();
    if (lenis && typeof lenis.stop === 'function') {
      lenis.stop();
      this.lenisArrete = true;
    }
  }

  fermer() {
    if (this.menu?.open) this.menu.close();
  }

  relancerLenis() {
    if (!this.lenisArrete) return;
    this.lenisArrete = false;
    const lenis = lenisActif();
    if (lenis && typeof lenis.start === 'function') lenis.start();
  }

  apresFermeture() {
    this.relancerLenis();
    // Le navigateur rend en général le focus au bouton « Menu » ; on le garantit.
    const actif = document.activeElement;
    if (this.ouvreur && (!actif || actif === document.body)) this.ouvreur.focus();
  }

  pieger(event) {
    if (event.key !== 'Tab' || !this.menu) return;
    const elements = [...this.menu.querySelectorAll(FOCALISABLES)].filter(
      (element) => element.getClientRects().length > 0,
    );
    if (elements.length === 0) return;
    const premier = elements[0];
    const dernier = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === premier) {
      event.preventDefault();
      dernier.focus();
    } else if (!event.shiftKey && document.activeElement === dernier) {
      event.preventDefault();
      premier.focus();
    }
  }

  majPanier(valeur) {
    const nombre = Number(valeur);
    if (!Number.isInteger(nombre) || nombre < 0) return;
    // Le libellé vient de la traduction (« Panier (1) ») : seul le nombre change.
    this.querySelectorAll('[data-mq-panier-compte]').forEach((element) => {
      element.textContent = String(nombre);
    });
  }
}

if (!customElements.get('mq-entete')) customElements.define('mq-entete', MqEntete);

class MqLangue extends HTMLElement {
  connectedCallback() {
    this.bouton = this.querySelector('[data-mq-langue-bouton]');
    this.liste = this.querySelector('[data-mq-langue-liste]');
    this.champ = this.querySelector('input[name="language_code"]');
    if (!this.bouton || !this.liste) return;

    if (!this.lie) {
      this.lie = true;
      this.surBouton = () => {
        if (this.ouvert()) this.fermer(false);
        else this.ouvrir();
      };
      this.surClavier = (event) => {
        if (event.key !== 'Escape' || !this.ouvert()) return;
        // Dans le menu mobile (<dialog> modal), Échap ne referme que la liste : le menu reste ouvert.
        event.preventDefault();
        event.stopPropagation();
        this.fermer(true);
      };
      this.surSortie = (event) => {
        const vers = event.relatedTarget;
        if (this.ouvert() && vers instanceof Node && !this.contains(vers)) this.fermer(false);
      };
      this.surChoix = (event) => {
        const choix = event.target instanceof Element ? event.target.closest('[data-mq-langue-code]') : null;
        if (!choix) return;
        const code = choix.getAttribute('data-mq-langue-code');
        if (!this.champ || !code || code === this.champ.value) {
          // Langue déjà affichée : rien à envoyer, la liste se referme.
          event.preventDefault();
          this.fermer(true);
          return;
        }
        this.champ.value = code; // le bouton est de type submit : le formulaire de localisation part ensuite
      };
      this.bouton.addEventListener('click', this.surBouton);
      this.addEventListener('keydown', this.surClavier);
      this.addEventListener('focusout', this.surSortie);
      this.liste.addEventListener('click', this.surChoix);
    }

    this.surDehors = (event) => {
      if (this.ouvert() && event.target instanceof Node && !this.contains(event.target)) this.fermer(false);
    };
    this.surFermetureMenu = () => this.fermer(false);
    this.dialogue = this.closest('dialog');
    document.addEventListener('pointerdown', this.surDehors);
    this.dialogue?.addEventListener('close', this.surFermetureMenu);
  }

  disconnectedCallback() {
    if (this.surDehors) document.removeEventListener('pointerdown', this.surDehors);
    if (this.surFermetureMenu) this.dialogue?.removeEventListener('close', this.surFermetureMenu);
  }

  ouvert() {
    return this.bouton.getAttribute('aria-expanded') === 'true';
  }

  ouvrir() {
    this.liste.hidden = false;
    this.bouton.setAttribute('aria-expanded', 'true');
  }

  fermer(rendreFocus) {
    if (!this.ouvert()) return;
    this.liste.hidden = true;
    this.bouton.setAttribute('aria-expanded', 'false');
    if (rendreFocus) this.bouton.focus();
  }
}

if (!customElements.get('mq-langue')) customElements.define('mq-langue', MqLangue);

function lierEnvoiAutomatique(racine = document) {
  racine.querySelectorAll('select[data-mq-envoi-auto]').forEach((select) => {
    if (select.dataset.mqEnvoiLie) return;
    select.dataset.mqEnvoiLie = 'oui';
    let auClavier = false;
    select.addEventListener('keydown', () => {
      auClavier = true;
    });
    select.addEventListener('pointerdown', () => {
      auClavier = false;
    });
    select.addEventListener('change', () => {
      // Au clavier, les flèches changent la valeur : la page ne change qu'avec « Valider » (WCAG 3.2.2).
      const formulaire = select.form;
      if (auClavier || !formulaire) return;
      if (typeof formulaire.requestSubmit === 'function') formulaire.requestSubmit();
      else formulaire.submit();
    });
  });
}

lierEnvoiAutomatique();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) lierEnvoiAutomatique(event.target);
});
